import { useAuthStore } from '../stores/authStore';

// Fallback points at PRODUCTION (relative '/api' breaks in the native app).
const API_URL = import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';
const API_BASE = `${API_URL}/v1`;

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = useAuthStore.getState().token;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        // One quick retry: a transient blip must not nuke the whole session
        if (!(options as any).__retried401) {
          await new Promise((r) => setTimeout(r, 400));
          return request<T>(endpoint, { ...options, __retried401: true } as any);
        }
        try {
          localStorage.setItem('mise-pos-last-401', `${options.method || 'GET'} ${endpoint}`);
        } catch { /* diagnostics only */ }
        // Only end the session when OUR token is genuinely expired/absent.
        // A 401 with a still-valid token is a server/middleware quirk — kill
        // the request, never the whole shift.
        let tokenExpired = true;
        try {
          if (token) {
            const { exp } = JSON.parse(atob(token.split('.')[1]));
            tokenExpired = !exp || exp * 1000 < Date.now();
          }
        } catch {
          tokenExpired = true;
        }
        if (tokenExpired) {
          useAuthStore.getState().logout(true);
        }
        return {
          success: false,
          error: {
            code: 'UNAUTHORIZED',
            message: tokenExpired
              ? 'Your session ended — please sign in again.'
              : "That didn't go through — please try again.",
          },
        };
      }
      return {
        success: false,
        error: data.error || { code: 'UNKNOWN', message: 'Request failed' },
      };
    }

    return data;
  } catch (error) {
    console.error('API request failed:', error);
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: 'Network request failed' },
    };
  }
}

// Tables
export const tablesApi = {
  getAll: () => request<any[]>('/tables'),
  getOne: (id: string) => request<any>(`/tables/${id}`),
  updateStatus: (id: string, status: string) =>
    request<any>(`/tables/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  clear: (id: string) =>
    request<any>(`/tables/${id}/clear`, { method: 'POST', body: JSON.stringify({}) }),
};

// Sections (for floor plan)
export const sectionsApi = {
  getAll: () => request<any[]>('/sections'),
};

// Menu
export const menuApi = {
  getAll: () => request<any>('/menu'),
  getItem: (id: string) => request<any>(`/menu/items/${id}`),
};

// Checks
export const checksApi = {
  getAll: (params?: { status?: string; tableId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.tableId) queryParams.append('tableId', params.tableId);
    const query = queryParams.toString();
    return request<any[]>(`/checks${query ? `?${query}` : ''}`);
  },
  open: (data: { tableId?: string; partySize: number; type?: string; guestName?: string; guestAllergens?: string[] }) =>
    request<any>('/checks', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        type: data.type || (data.tableId ? 'table' : 'bar'),
      }),
    }),
  getOne: (id: string) => request<any>(`/checks/${id}`),
  update: (id: string, data: { partySize?: number; guestAllergens?: string[]; notes?: string }) =>
    request<any>(`/checks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  addItems: (checkId: string, items: any[]) =>
    request<any>(`/checks/${checkId}/items`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),
  close: (id: string) =>
    request<any>(`/checks/${id}/close`, { method: 'POST' }),
  emailReceipt: (checkId: string, email: string) =>
    request<any>(`/checks/${checkId}/receipt`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  applyDiscount: (checkId: string, data: { discountType: string; value: number; reason?: string }) =>
    request<any>(`/checks/${checkId}/discount`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  transfer: (checkId: string, data: { toTableId?: string; toServerId?: string }) =>
    request<any>(`/checks/${checkId}/transfer`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  // Get course status for a check
  getCourses: (checkId: string) =>
    request<any>(`/checks/${checkId}/courses`),
  // Split a check
  split: (checkId: string, data: { splitType: string; numberOfWays?: number; splits?: any[] }) =>
    request<{ originalCheckId: string; splitType: string; splits: any[] }>(`/checks/${checkId}/split`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Orders
export const ordersApi = {
  create: (data: any) =>
    request<any>('/orders', { method: 'POST', body: JSON.stringify(data) }),
  fire: (orderId: string, options?: { isRush?: boolean; courseNumber?: number }) =>
    request<any>(`/orders/${orderId}/fire`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    }),
  send: (orderId: string) =>
    request<any>(`/orders/${orderId}/fire`, { method: 'POST' }),
  // Fire all items for a specific course
  fireCourse: (checkId: string, courseNumber: number, isRush?: boolean) =>
    request<any>('/orders/fire-course', {
      method: 'POST',
      body: JSON.stringify({ checkId, courseNumber, isRush }),
    }),
  // Fire specific items by ID
  fireItems: (itemIds: string[], isRush?: boolean) =>
    request<any>('/orders/items/fire', {
      method: 'POST',
      body: JSON.stringify({ itemIds, isRush }),
    }),
  voidItem: (itemId: string, reason: string, managerPin?: string) =>
    request<any>(`/orders/items/${itemId}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason, managerPin }),
    }),
  addItem: (orderId: string, item: { menuItemId: string; quantity: number; seatNumber?: number; modifiers?: any[]; specialInstructions?: string }) =>
    request<any>(`/orders/${orderId}/items`, {
      method: 'POST',
      body: JSON.stringify(item),
    }),
};

// Payments
export const paymentsApi = {
  // Create payment intent for card payments
  createIntent: (checkId: string, amount: number, tipAmount?: number, splitCheckId?: string) =>
    request<any>('/payments/intent', {
      method: 'POST',
      body: JSON.stringify({ checkId, amount: Math.round(amount), tipAmount: Math.round(tipAmount || 0), splitCheckId }),
    }),
  // Confirm payment after card is processed
  confirmPayment: (paymentIntentId: string) =>
    request<any>('/payments/confirm', {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId }),
    }),
  // Process cash payment
  processCash: (checkId: string, amountTendered: number, tipAmount?: number, splitCheckId?: string) =>
    request<any>('/payments/cash', {
      method: 'POST',
      // Cents are ALWAYS integers on the wire — a float or NaN here turns
      // into a schema rejection the user reads as "Validation failed".
      body: JSON.stringify({ checkId, amountTendered: Math.round(amountTendered), tipAmount: Math.round(tipAmount || 0), splitCheckId }),
    }),
  // Unified process method for backwards compatibility - routes to correct endpoint
  process: async (checkId: string, data: { method: string; amountCents: number; tipCents?: number; [key: string]: any }) => {
    if (data.method === 'cash') {
      return paymentsApi.processCash(checkId, data.amountCents, data.tipCents);
    } else {
      // For card payments, create intent (in real app, would then use Stripe Elements to confirm)
      return paymentsApi.createIntent(checkId, data.amountCents, data.tipCents);
    }
  },
  // Refund payment
  refund: (paymentId: string, amount: number, reason: string, managerPin?: string) =>
    request<any>(`/payments/${paymentId}/refund`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason, managerPin }),
    }),
  // Get tip balance for employee
  getTipBalance: () =>
    request<any>('/payments/tips/balance'),
  // Request tip payout
  requestPayout: (method: 'instant' | 'standard', amount?: number) =>
    request<any>('/payments/tips/payout', {
      method: 'POST',
      body: JSON.stringify({ method, amount }),
    }),
};

// Kitchen
export const kitchenApi = {
  getTickets: (station?: string) => {
    const query = station ? `?station=${station}` : '';
    return request<any[]>(`/kitchen/tickets${query}`);
  },
  getStats: () => request<any>('/kitchen/stats'),
  getReadyAlerts: (sinceSeconds = 180) =>
    request<{ alerts: Array<{ kind: 'ticket' | 'course'; orderId: string; checkId: string | null; tableNumber: string | null; courseNumber: number | null; readyAt: string }> }>(
      `/kitchen/ready-alerts?sinceSeconds=${sinceSeconds}`
    ),
};

// Kitchen Stations
export const kitchenStationsApi = {
  getAll: () => request<any[]>('/kitchen-stations'),
  getAvailableCooks: () => request<any[]>('/kitchen-stations/available-cooks'),
};

// Reservations
export const reservationsApi = {
  getAll: (params?: { date?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.date) queryParams.append('date', params.date);
    if (params?.status) queryParams.append('status', params.status);
    const query = queryParams.toString();
    return request<any[]>(`/reservations${query ? `?${query}` : ''}`);
  },
  getToday: () => {
    const today = new Date().toISOString().split('T')[0];
    return request<any[]>(`/reservations?date=${today}`);
  },
  seat: (id: string, tableId: string) =>
    request<any>(`/reservations/${id}/seat`, {
      method: 'POST',
      body: JSON.stringify({ tableId }),
    }),
  confirm: (id: string) =>
    request<any>(`/reservations/${id}/confirm`, { method: 'POST' }),
  cancel: (id: string, reason?: string) =>
    request<any>(`/reservations/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

// Guests
export const guestsApi = {
  getAll: (params?: { search?: string; isVip?: boolean; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.isVip !== undefined) queryParams.append('isVip', String(params.isVip));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    const query = queryParams.toString();
    return request<any[]>(`/guests${query ? `?${query}` : ''}`);
  },
  search: (q: string) => request<any[]>(`/guests/search?q=${encodeURIComponent(q)}`),
  getById: (id: string) => request<any>(`/guests/${id}`),
  create: (data: { firstName: string; lastName: string; email?: string; phone?: string; allergies?: string[]; isVIP?: boolean }) =>
    request<any>('/guests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: any) =>
    request<any>(`/guests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  markVip: (id: string, isVIP: boolean, notes?: string) =>
    request<any>(`/guests/${id}/vip`, {
      method: 'POST',
      body: JSON.stringify({ isVIP, notes }),
    }),
  getSuggestions: (guestId: string, limit?: number) => {
    const query = limit ? `?limit=${limit}` : '';
    return request<any>(`/guests/${guestId}/suggestions${query}`);
  },
};

// Feedback
export const feedbackApi = {
  // Get items from a check ready for feedback
  getCheckItems: (checkId: string) =>
    request<any>(`/feedback/check/${checkId}/items`),

  // Submit feedback for a check
  submit: (checkId: string, data: {
    itemFeedback: Array<{
      orderItemId?: string;
      menuItemId?: string;
      menuItemName: string;
      sentiment: 'loved' | 'liked' | 'disliked' | 'wont_order';
      comment?: string;
      seatNumber?: number;
      guestName?: string;
    }>;
    visitFeedback?: {
      overallRating?: number;
      foodRating?: number;
      serviceRating?: number;
      highlights?: string[];
      improvements?: string[];
      notes?: string;
      wouldRecommend?: boolean;
    };
    createGuestProfile?: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
    };
  }) =>
    request<any>(`/feedback/check/${checkId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Get feedback summary
  getSummary: (days?: number) => {
    const query = days ? `?days=${days}` : '';
    return request<any>(`/feedback/summary${query}`);
  },
};

// Shift Management
export const shiftApi = {
  // Get pre-shift preparation data
  getPreShiftData: (employeeId: string) =>
    request<any>(`/staff/${employeeId}/pre-shift`),

  // Get current shift status (duration, break status, etc.)
  getShiftStatus: (employeeId: string) =>
    request<{
      isClockedIn: boolean;
      timeEntry: {
        id: string;
        clockIn: string;
        shiftType: string;
        role: string;
        breakStart: string | null;
        breakMinutes: number;
      } | null;
      shiftDuration: number;
      currentBreakMinutes: number;
      onBreak: boolean;
    }>(`/staff/${employeeId}/shift-status`),

  // Clock in with shift type
  clockIn: (employeeId: string, data: { shiftType: string; role?: string }) =>
    request<any>(`/staff/${employeeId}/clock-in`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Start break
  startBreak: (employeeId: string, breakType?: string) =>
    request<any>(`/staff/${employeeId}/break/start`, {
      method: 'POST',
      body: JSON.stringify({ breakType }),
    }),

  // End break
  endBreak: (employeeId: string) =>
    request<any>(`/staff/${employeeId}/break/end`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // Update pre-shift checklist
  updateChecklist: (employeeId: string, data: {
    menuReviewed?: boolean;
    allergiesReviewed?: boolean;
    sectionConfirmed?: boolean;
    sectionId?: string;
  }) =>
    request<any>(`/staff/${employeeId}/pre-shift/checklist`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Complete pre-shift and start shift (clock in)
  startShift: (employeeId: string, data: {
    checklist: {
      menuReviewed?: boolean;
      allergiesReviewed?: boolean;
      sectionConfirmed?: boolean;
      reservationsReviewed?: boolean;
      eightySixReviewed?: boolean;
      stationConfirmed?: boolean;
      overviewReviewed?: boolean;
    };
    stationId?: string;
    sectionId?: string;
    role?: string;
  }) =>
    request<any>(`/staff/${employeeId}/pre-shift/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Get live shift stats
  getShiftSummary: (employeeId: string) =>
    request<any>(`/staff/${employeeId}/shift-summary`),

  // Get active announcements for staff
  getAnnouncements: (role?: string) =>
    request<any>(`/preshift-config/announcements${role ? `?role=${role}` : ''}`),

  // Get post-shift summary report
  getPostShiftSummary: (employeeId: string) =>
    request<any>(`/staff/${employeeId}/post-shift/summary`),

  // Submit post-shift notes and issues
  submitPostShiftReport: (employeeId: string, data: {
    notes?: string;
    issues?: { category: string; description: string; severity: string }[];
    cashTipsDeclaredCents?: number;
  }) =>
    request<any>(`/staff/${employeeId}/post-shift/report`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Clock out
  clockOut: (employeeId: string) =>
    request<any>(`/staff/${employeeId}/clock-out`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};

// Restaurant (shared device config, e.g. printers)
export const restaurantApi = {
  current: () => request<any>('/restaurants/current'),
  updateSettings: (settings: Record<string, unknown>) =>
    request<any>('/restaurants/current/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }),
};

// Stripe Terminal (Tap to Pay on iPhone)
export const terminalApi = {
  connectionToken: () =>
    request<any>('/terminal/connection-token', { method: 'POST' }),
  createPaymentIntent: (checkId: string, amount: number, tipAmount: number, splitCheckId?: string) =>
    request<any>('/terminal/payment-intent', {
      method: 'POST',
      body: JSON.stringify({ checkId, amount: Math.round(amount), tipAmount: Math.round(tipAmount || 0), splitCheckId }),
    }),
};

export { request };
